// =============================================================================
// /admin — operator-only dashboard (access-code gated)
// =============================================================================
// Enter the admin code once (kept in sessionStorage). Fetches /api/admin/data
// with an x-admin-code header. Shows totals, per-city breakdown (answers
// "how many in city X"), the full referral tree (who joined under whom), and a
// flat table. Names are shown here — this is the operator view, not public.
// =============================================================================

import { useEffect, useMemo, useRef, useState } from "react"
import { downloadAsPdf } from "@/lib/download-pdf"

interface Supporter {
  id: string
  first_name: string
  stage: "dot" | "flag" | "flag_t2" | "flag_t3"
  direct_invitees_count: number
  total_descendants_count: number
  referrer_id: string | null
  created_at: string
  city_name_he: string | null
  on_map: boolean
}

interface Incomplete {
  name: string
  email: string | null
  created_at: string
}

const CODE_KEY = "__degel_admin_code"
const NAVY = "#1B2D52"

export function AdminPage() {
  const [code, setCode] = useState<string>(() => sessionStorage.getItem(CODE_KEY) ?? "")
  const [input, setInput] = useState("")
  const [rows, setRows] = useState<Supporter[] | null>(null)
  const [incomplete, setIncomplete] = useState<Incomplete[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportingXlsx, setExportingXlsx] = useState(false)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const contentRef = useRef<HTMLDivElement>(null)

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  const today = () => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
  }

  async function exportPdf() {
    if (!contentRef.current) return
    setExporting(true)
    try {
      await downloadAsPdf(contentRef.current, `el-hadegel-signups-${today()}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : "שגיאת ייצוא")
    } finally {
      setExporting(false)
    }
  }

  // Excel comes from the server already built (/api/admin/export) — the admin
  // code travels in a header, so this is a fetch + blob download rather than a
  // plain link. It carries phone numbers and emails, which the on-screen table
  // does not.
  async function exportExcel() {
    setExportingXlsx(true)
    setError(null)
    try {
      const res = await fetch("/api/admin/export", { headers: { "x-admin-code": code } })
      if (res.status === 401) {
        setError("קוד שגוי")
        sessionStorage.removeItem(CODE_KEY)
        setCode("")
        setRows(null)
        return
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `el-hadegel-supporters-${today()}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e instanceof Error ? e.message : "שגיאת ייצוא")
    } finally {
      setExportingXlsx(false)
    }
  }

  async function load(theCode: string) {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch("/api/admin/data", { headers: { "x-admin-code": theCode } })
      if (res.status === 401) {
        setError("קוד שגוי")
        sessionStorage.removeItem(CODE_KEY)
        setCode("")
        setRows(null)
        return
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = (await res.json()) as { supporters: Supporter[]; incomplete?: Incomplete[] }
      sessionStorage.setItem(CODE_KEY, theCode)
      setCode(theCode)
      setRows(data.supporters)
      setIncomplete(data.incomplete ?? [])
    } catch (e) {
      setError(e instanceof Error ? e.message : "שגיאה")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (code) void load(code)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── derived views ──────────────────────────────────────────────────────────
  const byId = useMemo(() => new Map((rows ?? []).map((r) => [r.id, r])), [rows])
  const childrenOf = useMemo(() => {
    const m = new Map<string, Supporter[]>()
    for (const r of rows ?? []) {
      if (r.referrer_id && byId.has(r.referrer_id)) {
        const a = m.get(r.referrer_id) ?? []
        a.push(r)
        m.set(r.referrer_id, a)
      }
    }
    return m
  }, [rows, byId])
  const roots = useMemo(
    () => (rows ?? []).filter((r) => !r.referrer_id || !byId.has(r.referrer_id)),
    [rows, byId],
  )
  // Every node that has children — used by the collapse-all / expand-all switch.
  const parentIds = useMemo(() => [...childrenOf.keys()], [childrenOf])
  const allCollapsed = parentIds.length > 0 && parentIds.every((id) => collapsed.has(id))
  const cities = useMemo(() => {
    const m = new Map<string, { count: number; onMap: number }>()
    for (const r of rows ?? []) {
      const key = r.city_name_he ?? "— ללא עיר —"
      const e = m.get(key) ?? { count: 0, onMap: 0 }
      e.count++
      if (r.on_map) e.onMap++
      m.set(key, e)
    }
    return [...m.entries()].map(([city, v]) => ({ city, ...v })).sort((a, b) => b.count - a.count)
  }, [rows])

  // ── gate ─────────────────────────────────────────────────────────────────
  if (!code || (!rows && !loading)) {
    return (
      <div dir="rtl" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#F8FAFC", fontFamily: "Heebo, system-ui, sans-serif" }}>
        <form
          onSubmit={(e) => { e.preventDefault(); if (input.trim()) void load(input.trim()) }}
          style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 16, padding: 32, width: 360, boxShadow: "0 10px 40px rgba(27,45,82,0.08)" }}
        >
          <div style={{ fontSize: 20, fontWeight: 800, color: NAVY, marginBottom: 4 }}>אל הדגל — ניהול</div>
          <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 20 }}>הזן/י קוד גישה</div>
          <input
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="קוד גישה"
            autoFocus
            style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: 10, fontSize: 15, marginBottom: 12, boxSizing: "border-box" }}
          />
          {error && <div style={{ color: "#DC2626", fontSize: 13, marginBottom: 12 }}>{error}</div>}
          <button type="submit" disabled={loading} style={{ width: "100%", padding: "10px 12px", background: NAVY, color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
            {loading ? "טוען…" : "כניסה"}
          </button>
        </form>
      </div>
    )
  }

  const stat = (label: string, value: string | number) => (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "14px 18px", minWidth: 120 }}>
      <div style={{ fontSize: 24, fontWeight: 800, color: NAVY }}>{value}</div>
      <div style={{ fontSize: 12, color: "#6B7280" }}>{label}</div>
    </div>
  )

  const TreeNode = ({ node, depth }: { node: Supporter; depth: number }) => {
    const kids = childrenOf.get(node.id) ?? []
    const hasKids = kids.length > 0
    const isCollapsed = collapsed.has(node.id)
    return (
      <div style={{ marginRight: depth * 22 }}>
        {/* NOT flex — a single non-wrapping inline line (width:max-content), so
            html-to-image in Safari can't break the city name onto two lines.
            Clicking a node with children collapses/expands its subtree. */}
        <div
          onClick={hasKids ? () => toggle(node.id) : undefined}
          style={{ padding: "4px 0", borderBottom: "1px solid #F1F5F9", whiteSpace: "nowrap", width: "max-content", maxWidth: "100%", fontSize: 14, lineHeight: 1.6, cursor: hasKids ? "pointer" : "default", userSelect: "none" }}
        >
          <span style={{ fontSize: 11, color: hasKids ? NAVY : "#9CA3AF", display: "inline-block", width: 12 }}>{hasKids ? (isCollapsed ? "▸" : "▾") : "•"}</span>{" "}
          <span style={{ fontWeight: 600, color: "#0F1B36" }}>{node.first_name}</span>{" "}
          <span style={{ fontSize: 12, color: "#6B7280" }}>· {node.city_name_he ?? "—"}</span>
          {node.direct_invitees_count > 0 && (
            <span style={{ fontSize: 11, color: NAVY, background: "rgba(27,45,82,0.08)", borderRadius: 999, padding: "1px 8px", marginRight: 8, display: "inline-block", verticalAlign: "middle" }}>
              {node.direct_invitees_count} הזמינ/ה
            </span>
          )}
          {!node.on_map && <span style={{ fontSize: 10, color: "#B45309", background: "#FEF3C7", borderRadius: 999, padding: "1px 6px", marginRight: 6, display: "inline-block", verticalAlign: "middle" }}>לא על המפה</span>}
        </div>
        {hasKids && !isCollapsed && kids.map((k) => <TreeNode key={k.id} node={k} depth={depth + 1} />)}
      </div>
    )
  }

  return (
    <div dir="rtl" style={{ minHeight: "100vh", background: "#F8FAFC", fontFamily: "Heebo, system-ui, sans-serif", padding: "28px 20px" }}>
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: NAVY }}>אל הדגל — לוח ניהול</div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={exportExcel} disabled={exportingXlsx} title="כולל טלפונים ואימיילים — מידע אישי" style={{ fontSize: 13, fontWeight: 700, color: "#fff", background: "#107C41", border: "none", borderRadius: 8, padding: "7px 16px", cursor: exportingXlsx ? "wait" : "pointer" }}>{exportingXlsx ? "מייצא…" : "⬇︎ ייצוא Excel"}</button>
            <button onClick={exportPdf} disabled={exporting} style={{ fontSize: 13, fontWeight: 700, color: "#fff", background: NAVY, border: "none", borderRadius: 8, padding: "7px 16px", cursor: exporting ? "wait" : "pointer" }}>{exporting ? "מייצא…" : "⬇︎ ייצוא PDF"}</button>
            <button onClick={() => { sessionStorage.removeItem(CODE_KEY); setCode(""); setRows(null) }} style={{ fontSize: 13, color: "#6B7280", background: "none", border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 12px", cursor: "pointer" }}>יציאה</button>
          </div>
        </div>

        <div ref={contentRef} style={{ background: "#F8FAFC" }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 26 }}>
          {stat("סה״כ נרשמו", rows?.length ?? 0)}
          {stat("על המפה", (rows ?? []).filter((r) => r.on_map).length)}
          {stat("לא על המפה", (rows ?? []).filter((r) => !r.on_map).length)}
          {stat("ערים", cities.length)}
          {stat("נכנסו ולא סיימו", incomplete.length)}
        </div>

        {/* Logged in with Google but never finished signup */}
        {incomplete.length > 0 && (
          <div style={{ background: "#fff", border: "1px solid #FDE68A", borderRadius: 14, padding: 20, marginBottom: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#B45309", marginBottom: 4 }}>התחברו אך לא השלימו הרשמה ({incomplete.length})</div>
            <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 12 }}>נכנסו עם Google אך לא בחרו עיר / לא סיימו — לא נספרים ולא על המפה</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ textAlign: "right", color: "#6B7280", fontSize: 12 }}>
                  <th style={{ padding: "6px 4px" }}>שם (מ‑Google)</th>
                  <th style={{ padding: "6px 4px" }}>אימייל</th>
                  <th style={{ padding: "6px 4px" }}>נכנס/ה בתאריך</th>
                </tr>
              </thead>
              <tbody>
                {incomplete.map((u, i) => (
                  <tr key={i} style={{ borderTop: "1px solid #F1F5F9" }}>
                    <td style={{ padding: "6px 4px", fontWeight: 600, color: "#0F1B36" }}>{u.name}</td>
                    <td style={{ padding: "6px 4px", color: "#6B7280", direction: "ltr", textAlign: "right" }}>{u.email ?? "—"}</td>
                    <td style={{ padding: "6px 4px", color: "#9CA3AF" }}>{new Date(u.created_at).toLocaleDateString("he-IL")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* City breakdown */}
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, padding: 20, marginBottom: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 12 }}>לפי עיר</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: "right", color: "#6B7280", fontSize: 12 }}>
                <th style={{ padding: "6px 4px" }}>עיר</th>
                <th style={{ padding: "6px 4px" }}>נרשמו</th>
                <th style={{ padding: "6px 4px" }}>על המפה</th>
              </tr>
            </thead>
            <tbody>
              {cities.map((c) => (
                <tr key={c.city} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "6px 4px", fontWeight: 600, color: "#0F1B36" }}>{c.city}</td>
                  <td style={{ padding: "6px 4px" }}>{c.count}</td>
                  <td style={{ padding: "6px 4px", color: c.onMap < c.count ? "#B45309" : "#059669" }}>{c.onMap}/{c.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Referral tree */}
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, padding: 20, marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: NAVY }}>עץ ההזמנות</div>
            <button
              onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(parentIds))}
              style={{ fontSize: 12, fontWeight: 600, color: NAVY, background: "none", border: "1px solid #E5E7EB", borderRadius: 8, padding: "4px 12px", cursor: "pointer" }}
            >
              {allCollapsed ? "פתח הכל" : "כווץ הכל"}
            </button>
          </div>
          <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 12 }}>מי הצטרף דרך מי · לחצו על שם כדי לכווץ ({roots.length} שורשים)</div>
          <div style={{ fontSize: 14 }}>
            {roots.map((r) => <TreeNode key={r.id} node={r} depth={0} />)}
          </div>
        </div>

        {/* Flat table */}
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, padding: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 12 }}>כל הנרשמים ({rows?.length ?? 0})</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 640 }}>
              <thead>
                <tr style={{ textAlign: "right", color: "#6B7280", fontSize: 11 }}>
                  <th style={{ padding: "6px 4px" }}>#</th>
                  <th style={{ padding: "6px 4px" }}>שם</th>
                  <th style={{ padding: "6px 4px" }}>עיר</th>
                  <th style={{ padding: "6px 4px" }}>שלב</th>
                  <th style={{ padding: "6px 4px" }}>הזמין/ה</th>
                  <th style={{ padding: "6px 4px" }}>סה״כ תחתיו</th>
                  <th style={{ padding: "6px 4px" }}>הוזמן/ה ע״י</th>
                  <th style={{ padding: "6px 4px" }}>תאריך</th>
                </tr>
              </thead>
              <tbody>
                {(rows ?? []).map((r, i) => (
                  <tr key={r.id} style={{ borderTop: "1px solid #F1F5F9" }}>
                    <td style={{ padding: "6px 4px", color: "#9CA3AF" }}>{i + 1}</td>
                    <td style={{ padding: "6px 4px", fontWeight: 600, color: "#0F1B36" }}>{r.first_name}</td>
                    <td style={{ padding: "6px 4px" }}>{r.city_name_he ?? "—"}{!r.on_map && " ⚠︎"}</td>
                    <td style={{ padding: "6px 4px" }}>{r.stage}</td>
                    <td style={{ padding: "6px 4px" }}>{r.direct_invitees_count}</td>
                    <td style={{ padding: "6px 4px" }}>{r.total_descendants_count}</td>
                    <td style={{ padding: "6px 4px", color: "#6B7280" }}>{r.referrer_id ? (byId.get(r.referrer_id)?.first_name ?? "—") : "—"}</td>
                    <td style={{ padding: "6px 4px", color: "#9CA3AF" }}>{new Date(r.created_at).toLocaleDateString("he-IL")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </div>
    </div>
  )
}
